import { Box, Typography, useTheme } from "@mui/material";

import { ResponsiveRadialBar } from "@nivo/radial-bar";
import LegendsCharts from "../../legends";
import { useEffect, useRef, useState } from "react";
import useStyles from "../styles";
import handleColors from "../utils/handleColors";
import TooltipCharts from "../../toolTipCharts";
import { MyRadialDashConfig } from "./types";
import maskUnits from "../../../utils/maskUnits";
import getColorsForScheme from "../../legends/utils/getColorsForScheme";
import { getColorsArray } from "../../../utils/getColorsPallet";
import calculateMetrics from "../utils/calculateMetrics";

const formatSeries = (data: MyDatum[], indexBy: string, keys: string[]) =>
  data.map((item) => ({
    id: item[indexBy]?.toString(), // O nome da chave será o id
    data:
      keys.length > 1
        ? keys.map((key) => ({
            x: key,
            y: Number(item[key]),
          }))
        : [
            {
              x: keys[0].toString(),
              y: Number(item[keys[0]]),
            },
          ],
  }));

const RadialChart: React.FC<MyRadialDashConfig> = (props) => {
  const {
    data,
    keys,
    indexBy,
    units,
    fractionDigits,
    colors,
    metricsType,
    ...lineProps
  } = props;

  const formattedData =
    keys && keys.length > 0 ? formatSeries(data, indexBy, keys) : [];

  const newKeysLegends = formattedData.map((item) => item.id);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);

  const ref = useRef(null);
  const refTotal = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [innerRadius, setInnerRadius] = useState(0);
  const styles = useStyles(theme);
  useEffect(() => {
    if (ref.current) {
      const { offsetHeight, offsetWidth } = ref.current || {};
      setBoxHeight(offsetHeight);
      setBoxWidth(offsetWidth);
    }
  }, [ref.current]);
  useEffect(() => {
    if (refTotal.current) {
      const offsetHeight = refTotal.current?.offsetWidth;
      setInnerRadius((offsetHeight / 25) * 0.05 + 0.3);
    }
  }, [refTotal]);
  const defaultBarChartConfig = {
    margin: { top: 0, right: 0, bottom: 0, left: 0 },
    padding: 0.5,
    startAngle: 0,
    endAngle: 360,
    cornerRadius: 12,
    innerRadius: innerRadius,
    enableRadialGrid: false,
    enableCircularGrid: false,
    circularAxisOuter: null,
    radialAxisStart: null,
    tracksColor: theme.palette.background.textField,
  };
  const valueTotal =
    keys && keys.length == 1
      ? data.reduce((acc: number, item) => acc + Number(item[keys[0]]), 0)
      : null;

  const mergedProps = { ...defaultBarChartConfig, ...lineProps };
  const numItems = formattedData.length;

  const radialColors = getColorsArray(
    newKeysLegends.length,
    props.colors,
    theme.palette.mode === "dark"
  );

  return (
    <Box sx={styles.boxContainer}>
      <Box sx={styles.boxContainerSizeChart} ref={ref}>
        <Box sx={styles.boxChart(boxHeight, boxWidth)}>
          <ResponsiveRadialBar
            data={formattedData}
            {...mergedProps}
            {...(props.colorsByKey
              ? {
                  colors: (params) =>
                    handleColors(params.groupId, theme, props.colorsByKey),
                }
              : props.colors
              ? {
                  colors: (params) =>
                    (colors &&
                      radialColors &&
                      formattedData &&
                      radialColors[
                        formattedData.findIndex(
                          (data) => data.id === params.groupId
                        )
                      ]) ||
                    "",
                }
              : {})}
            tooltip={({ bar }) => (
              <TooltipCharts
                valueX={bar.groupId}
                valueY={keys.length > 1 ? bar.category : ""}
                valueZ={maskUnits(bar.value, units, fractionDigits)}
                color={bar.color}
              />
            )}
          />
        </Box>

        <Box sx={styles.boxLabelRadial} ref={refTotal}>
          {valueTotal && (
            <>
              <Typography
                sx={{ fontWeight: 400, fontSize: "0.875rem" }}
                color="text.secondary"
              >
                Total
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                {maskUnits(valueTotal, units, fractionDigits)}
                {/* {props?.unit ? ` ${props.unit}` : ""} */}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.boxLegendRadial,
          minHeight: metricsType ? "45px" : "25px",
        }}
      >
        <LegendsCharts
          {...props}
          legendSpacing={1}
          keys={[...(newKeysLegends || [])]}
          metricsType={
            metricsType
              ? calculateMetrics(metricsType, data as any[])
              : undefined
          }
          position="center"
        />
      </Box>
    </Box>
  );
};

export default RadialChart;
